import config from 'Cloud/config';
import { type IExtInfo, extInfo } from 'lib/extInfo';
import { getFaviconByExtension } from 'lib/getFaviconByExtension';
import React, { memo, Suspense, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { usePortal } from 'reactApp/hooks/usePortal';
import { historyPush } from 'reactApp/modules/router/router.module';
import type { IHistoryPush } from 'reactApp/modules/router/router.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { getViewerItem } from 'reactApp/modules/viewer/viewer.selectors';
import { noop } from 'reactApp/utils/helpers';
import { replaceFaviconTags } from 'reactApp/utils/replaceFaviconTags';
import { closeViewerUrl } from 'reactApp/utils/urlHelper';

export const ReactViewerConnected = React.lazy(() =>
    Promise.all([
        import(/* webpackChunkName: "reactViewer" */ '../../../ui/ReactViewer/ReactViewer'),
        import(/* webpackPreload: true, webpackChunkName: "lottie" */ 'lottie-react'),
    ]).then(([reactViewer]) => reactViewer)
);
export const ViewerRender = memo<{ selectedViewItemId: string }>(({ selectedViewItemId }) => {
    const reactViewerTarget = usePortal('ReactViewer');
    const dispatch = useDispatch();

    const item = useSelector(getViewerItem);

    const buildUrls = config.get('BUILD_URLS');

    const fileExtension = item && 'ext' in item ? item?.ext : '';
    const extInf: IExtInfo = extInfo.get(fileExtension);
    const favicons = getFaviconByExtension(extInf, buildUrls.img);
    replaceFaviconTags(favicons);

    const onViewerClose = useCallback(() => {
        const favicons = getFaviconByExtension(null, buildUrls.img);
        replaceFaviconTags(favicons);

        let closeUrl = item?.storage === EStorageType.stock ? `/stock/${item.parent}` : closeViewerUrl(location);
        closeUrl = item?.storage === EStorageType.duplicates ? item.parent : closeUrl;

        const historyPushParams: IHistoryPush = { id: closeUrl };
        if (closeUrl.startsWith('/search') || location.search.includes('dialog=')) {
            historyPushParams.search = location.search;
        }
        // TODO: routing
        dispatch(historyPush(historyPushParams));
    }, [item?.storage, item?.parent]);

    // Для печати просмотрщика скрываем страницу, а его рендерим в корень
    return createPortal(
        <Suspense fallback={null}>
            <ReactViewerConnected
                file={{ id: selectedViewItemId }}
                isMountedFolder={false}
                showEditorHeader
                showNotify={noop}
                gaCategory="fv"
                onClose={onViewerClose}
                isViewer
                enablePreload
            />
        </Suspense>,
        reactViewerTarget
    );
});

ViewerRender.displayName = 'ViewerRender';
